import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { applyPricing, getProductDetails, getProductPricing } from '../api/products';
import { addToWishlist, removeFromWishlist, getWishlist } from '../api/wishlist';
import {
  getReviewsByProductId,
  createReview,
  updateReview,
  deleteReview,
  Review,
  ReviewInput,
} from '../api/reviews';
import { Product, ProductPricing, CATEGORIES, FRAME_TYPES, FRAME_SHAPES, IDEAL_FOR } from '../types';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTryonEnabled } from '../context/OrgContext';
import { canTryOn } from '../lib/tryon/frameUtils';
import TryonModal from '../components/tryon/TryonModal';
import StarRating from '../components/StarRating';

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

const InteractiveStars: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => (
  <span style={{ display: 'inline-flex', gap: 4 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <svg
        key={i}
        width={24}
        height={24}
        viewBox="0 0 24 24"
        fill={i <= value ? '#f59e0b' : 'none'}
        stroke="#f59e0b"
        strokeWidth="1.5"
        style={{ cursor: 'pointer' }}
        onClick={() => onChange(i)}
      >
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </span>
);

// Treat "NaN" / "null" / empty strings (and nullish) as "no value" so we don't
// render junk fields like "Frame Color: NaN" for non-frame products.
const hasValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== 'nan' && s !== 'null' && s !== 'undefined';
};

// Frame-based products that legitimately have frame color / type / shape.
const FRAME_CATEGORIES = [1, 3, 6]; // Eyeglasses, Sunglasses, Blue Light Glasses

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, openLogin } = useAuth();

  // What the product IS (cached for the session) and what it costs / how many are left (fetched
  // fresh on every visit). The page renders from the details; price and stock fill in when the
  // live pricing lands, and nothing can be bought before then.
  const [details, setDetails] = useState<Product | null>(null);
  const [pricing, setPricing] = useState<ProductPricing | null>(null);
  const [pricingFailed, setPricingFailed] = useState(false);
  const product = useMemo(
    () => (details && pricing ? applyPricing(details, pricing) : details),
    [details, pricing]
  );
  const pricingReady = pricing !== null;
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [quantity, setQuantity] = useState(1);
  const [wishlisted, setWishlisted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'info' | 'reviews'>('description');

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [tryonOpen, setTryonOpen] = useState(false);
  const tryonEnabled = useTryonEnabled();

  useEffect(() => {
    if (!id) return;
    const productId = Number(id);
    const ctrl = new AbortController();
    setLoading(true);
    setPricing(null);
    setPricingFailed(false);
    // Both halves at once, so the live price costs no extra wait over the old single call.
    getProductDetails(productId).then((p) => {
      if (ctrl.signal.aborted) return;
      setDetails(p);
      setWishlisted(!!p?.is_wishlisted);
      setLoading(false);
    });
    getProductPricing(productId, ctrl.signal).then((live) => {
      if (ctrl.signal.aborted) return;
      if (live) setPricing(live);
      else setPricingFailed(true);
    });
    return () => ctrl.abort();
  }, [id]);

  // Keyed on the id, not the object: `product` is rebuilt when pricing lands, which must not
  // fetch the reviews a second time.
  const productId = product?.id;
  useEffect(() => {
    if (productId == null) return;
    const loadReviews = async () => {
      const data = await getReviewsByProductId(productId);
      setReviews(data);
      const uid = user?.userId;
      if (uid) setHasReviewed(data.some((r) => r.user_id === uid));
    };
    loadReviews();
  }, [productId, user]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const imgs = product?.image_uri || [];
    const count = Math.max(imgs.length, 1);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setLightboxOpen(false); setZoomed(false); }
      else if (e.key === 'ArrowRight') { setZoomed(false); setSelectedImage((i) => (i + 1) % count); }
      else if (e.key === 'ArrowLeft') { setZoomed(false); setSelectedImage((i) => (i - 1 + count) % count); }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, product]);

  const refreshReviews = async () => {
    if (!product) return;
    const data = await getReviewsByProductId(product.id);
    setReviews(data);
    const uid = user?.userId;
    if (uid) setHasReviewed(data.some((r) => r.user_id === uid));
  };

  const handleSubmitReview = async () => {
    if (!user) { openLogin(handleSubmitReview); return; }
    if (reviewRating === 0 || !reviewComment.trim()) {
      alert('Please provide a rating and comment.');
      return;
    }
    setIsSubmitting(true);
    const payload: ReviewInput = {
      product_id: product!.id,
      rating: reviewRating,
      title: RATING_LABELS[reviewRating],
      comment: reviewComment.trim(),
    };
    let ok = false;
    if (isEditing && editingId) {
      ok = await updateReview(editingId, payload);
    } else {
      const reviewId = await createReview(payload);
      ok = !!reviewId;
    }
    if (ok) {
      await refreshReviews();
      setReviewRating(0);
      setReviewComment('');
      setIsEditing(false);
      setEditingId(null);
    } else {
      alert('Failed to submit review. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleEditReview = (review: Review) => {
    setReviewRating(review.rating);
    setReviewComment(review.comment);
    setIsEditing(true);
    setEditingId(review.id);
    setHasReviewed(false);
    document.getElementById('review-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Delete this review?')) return;
    const ok = await deleteReview(reviewId);
    if (ok) {
      if (editingId === reviewId) {
        setIsEditing(false);
        setEditingId(null);
        setReviewRating(0);
        setReviewComment('');
      }
      await refreshReviews();
    } else {
      alert('Failed to delete review.');
    }
  };

  const avgRating = reviews.length
    ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
    : 0;

  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((r) => { distribution[r.rating as keyof typeof distribution]++; });

  const visibleReviews = showAll ? reviews : reviews.slice(0, 3);

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="product-detail__skeleton">
          <div className="skeleton" style={{ height: 400 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 32, marginBottom: 12 }} />
            <div className="skeleton" style={{ height: 20, width: '60%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="empty-state">
          <p>Product not found.</p>
          <button className="btn btn--primary" onClick={() => navigate('/products')}>Back to Products</button>
        </div>
      </div>
    );
  }

  const images = product.image_uri || [];
  const isFrameProduct = FRAME_CATEGORIES.includes(product.category_id);
  // Non-frame products (e.g. contact lenses) carry their colour in product_attr
  const attrColor = product.product_attr?.color_update;
  const finalPrice = product.selling_price;
  const originalPrice = product.discount
    ? Math.round(product.selling_price / (1 - product.discount / 100))
    : null;
  // Until live stock lands nothing is known to be out of stock, but nothing can be bought either.
  const isOutOfStock = pricingReady && product.quantity === 0;
  const canBuy = pricingReady && !isOutOfStock;

  const handleAddToCart = async () => {
    if (!canBuy) return;
    if (!user) { openLogin(); return; }
    setAdding(true);
    const ok = await addToCart(product.id, quantity);
    setAdding(false);
    if (ok) { setAdded(true); setTimeout(() => setAdded(false), 2000); }
  };

  const handleBuyNow = async () => {
    if (!canBuy) return;
    if (!user) { openLogin(); return; }
    setBuyingNow(true);
    await addToCart(product.id, quantity);
    navigate('/cart');
  };

  const handleWishlist = async () => {
    if (!user) { openLogin(); return; }
    if (wishlisted) {
      const list = await getWishlist();
      const item = list.find((w) => w.product_id === product.id);
      if (item) { await removeFromWishlist(item.wishlist_id); setWishlisted(false); }
    } else {
      await addToWishlist(product.id);
      setWishlisted(true);
    }
  };

  return (
    <div className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <Link to="/products">{CATEGORIES[product.category_id] || 'Products'}</Link>
        <span className="breadcrumb__sep">›</span>
        <span>{product.product_name}</span>
      </div>

      <div className="product-detail__layout">
        {/* Image gallery */}
        <div className="product-detail__gallery">
          <div className="gallery__thumbnails">
            {images.map((img, i) => (
              <button
                key={i}
                className={`gallery__thumb ${selectedImage === i ? 'gallery__thumb--active' : ''}`}
                onClick={() => setSelectedImage(i)}
              >
                <img src={img} alt={`${product.product_name} ${i + 1}`} />
              </button>
            ))}
          </div>
          <div className="gallery__main">
            {images.length > 0 ? (
              <img
                src={images[selectedImage]}
                alt={product.product_name}
                className="gallery__main-image"
                style={{ cursor: 'zoom-in' }}
                onClick={() => { setZoomed(false); setLightboxOpen(true); }}
                title="Click to zoom"
              />
            ) : (
              <div className="gallery__placeholder">No image available</div>
            )}
          </div>
        </div>

        {/* Product info */}
        <div className="product-detail__info">
          {hasValue(product.brand) && <p className="product-detail__brand">{product.brand}</p>}
          <h1 className="product-detail__name">{product.product_name}</h1>

          {product.rating != null && product.rating > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
              <StarRating rating={product.rating} size={16} />
              <span style={{ color: '#6b7280', fontSize: 14 }}>({product.rating})</span>
            </div>
          )}

          <div className="product-detail__price-block" aria-busy={!pricingReady}>
            {pricingReady ? (
              <>
                <span className="product-detail__price">₹{Math.round(finalPrice).toLocaleString()}</span>
                {originalPrice && (
                  <>
                    <span className="product-detail__original">₹{originalPrice.toLocaleString()}</span>
                    <span className="product-detail__discount">{product.discount}% OFF</span>
                  </>
                )}
              </>
            ) : pricingFailed ? (
              <span style={{ color: '#6b7280', fontSize: 14 }}>Price unavailable right now. Please refresh to try again.</span>
            ) : (
              <span className="product-detail__price" style={{ color: 'transparent', background: '#f3f4f6', borderRadius: 6, minWidth: 96, display: 'inline-block' }}>₹0000</span>
            )}
          </div>

          {isFrameProduct && hasValue(product.colour) && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Frame Color:</span>
              <span className="product-detail__attr-value">{product.colour}</span>
            </div>
          )}
          {!isFrameProduct && hasValue(attrColor) && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Color:</span>
              <span className="product-detail__attr-value">{attrColor}</span>
            </div>
          )}
          {hasValue(product.size) && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Size:</span>
              <span className="product-detail__attr-value">{product.size}</span>
            </div>
          )}
          {isFrameProduct && product.frame_type_id && FRAME_TYPES[product.frame_type_id] && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Frame Type:</span>
              <span className="product-detail__attr-value">{FRAME_TYPES[product.frame_type_id]}</span>
            </div>
          )}
          {isFrameProduct && product.frame_shape_id && FRAME_SHAPES[product.frame_shape_id] && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Frame Shape:</span>
              <span className="product-detail__attr-value">{FRAME_SHAPES[product.frame_shape_id]}</span>
            </div>
          )}
          {product.ideal_for && (
            <div className="product-detail__attr-row">
              <span className="product-detail__attr-label">Ideal For:</span>
              <span className="product-detail__attr-value">{IDEAL_FOR[product.ideal_for] || product.ideal_for}</span>
            </div>
          )}

          <div className="product-detail__quantity">
            <span className="product-detail__attr-label">Quantity:</span>
            <div className="quantity-stepper">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1 || !canBuy}>−</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(Math.min(product.quantity, quantity + 1))} disabled={quantity >= product.quantity || !canBuy}>+</button>
            </div>
            {pricingReady && product.quantity > 0 && (
              <span style={{ fontSize: 13, color: '#16a34a' }}>{product.quantity} in stock</span>
            )}
          </div>

          {isOutOfStock && (
            <p style={{ color: '#dc2626', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              ⚠ This product is currently out of stock
            </p>
          )}

          <div className="product-detail__actions">
            <button
              className={`btn btn--primary ${added ? 'btn--success' : ''}`}
              onClick={handleAddToCart}
              disabled={adding || !canBuy}
            >
              {adding ? 'Adding...' : added ? '✓ Added to Cart' : 'Add to Cart'}
            </button>
            <button className="btn btn--outline" onClick={handleBuyNow} disabled={buyingNow || !canBuy}>
              {buyingNow ? 'Adding...' : 'Buy Now'}
            </button>
            {tryonEnabled && canTryOn(product) && (
              <button className="btn btn--tryon" onClick={() => setTryonOpen(true)} title="Try this frame on with your camera">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><circle cx="7" cy="12" r="4" /><circle cx="17" cy="12" r="4" /><line x1="11" y1="12" x2="13" y2="12" /></svg>
                Virtual Try-On
              </button>
            )}
            <button
              className={`wishlist-btn ${wishlisted ? 'wishlist-btn--active' : ''}`}
              onClick={handleWishlist}
              title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill={wishlisted ? '#ef4444' : 'none'} stroke={wishlisted ? '#ef4444' : 'currentColor'} strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {wishlisted ? 'Wishlisted' : 'Add to Wishlist'}
            </button>
          </div>

          {/* <div className="product-detail__trust">
            {[
              { icon: '🛡️', text: '2 Years Warranty' },
              // { icon: '🚚', text: 'Free Shipping on ₹500+' },
            ].map((b) => (
              <div key={b.text} className="product-detail__trust-item">
                <span>{b.icon}</span>
                <span>{b.text}</span>
              </div>
            ))}
          </div> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="product-detail__tabs">
        <div className="tabs__nav">
          {(['description', 'info', 'reviews'] as const).map((tab) => (
            <button
              key={tab}
              className={`tabs__btn ${activeTab === tab ? 'tabs__btn--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'description' ? 'Description' : tab === 'info' ? 'Additional Information' : `Reviews (${reviews.length})`}
            </button>
          ))}
        </div>

        <div className="tabs__content">
          {activeTab === 'description' && (
            <p>{product.product_desc || 'No description available.'}</p>
          )}

          {activeTab === 'info' && (
            <table className="info-table">
              <tbody>
                {hasValue(product.brand) && <tr><td>Brand</td><td>{product.brand}</td></tr>}
                {product.category_id && <tr><td>Category</td><td>{CATEGORIES[product.category_id]}</td></tr>}
                {isFrameProduct && product.frame_type_id && FRAME_TYPES[product.frame_type_id] && <tr><td>Frame Type</td><td>{FRAME_TYPES[product.frame_type_id]}</td></tr>}
                {isFrameProduct && product.frame_shape_id && FRAME_SHAPES[product.frame_shape_id] && <tr><td>Frame Shape</td><td>{FRAME_SHAPES[product.frame_shape_id]}</td></tr>}
                {isFrameProduct && hasValue(product.colour) && <tr><td>Color</td><td>{product.colour}</td></tr>}
                {!isFrameProduct && hasValue(attrColor) && <tr><td>Color</td><td>{attrColor}</td></tr>}
                {hasValue(product.size) && <tr><td>Size</td><td>{product.size}</td></tr>}
                {product.ideal_for && <tr><td>Ideal For</td><td>{IDEAL_FOR[product.ideal_for]}</td></tr>}
              </tbody>
            </table>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-section">

              {/* Rating Overview — only shown when reviews exist */}
              {reviews.length > 0 && (
                <>
                  <div className="reviews-overview">
                    <div className="reviews-overview__score">
                      <span className="reviews-overview__avg">{avgRating.toFixed(1)}</span>
                      <StarRating rating={avgRating} size={20} />
                      <span className="reviews-overview__count">
                        {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="reviews-overview__bars">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const pct = (distribution[star as keyof typeof distribution] / reviews.length) * 100;
                        return (
                          <div key={star} className="rating-bar">
                            <span className="rating-bar__label">{star}</span>
                            <svg width={12} height={12} viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5">
                              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                            </svg>
                            <div className="rating-bar__track">
                              <div className="rating-bar__fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="rating-bar__count">{distribution[star as keyof typeof distribution]}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <hr className="reviews-divider" />
                </>
              )}

              {/* Write / Edit Review Form */}
              {!hasReviewed && (
                <div id="review-form" className="review-form">
                  <h3 className="review-form__title">{isEditing ? 'Edit Your Review' : 'Write a Review'}</h3>
                  <div className="review-form__row">
                    <span className="review-form__label">Your Rating *</span>
                    <InteractiveStars value={reviewRating} onChange={setReviewRating} />
                    {reviewRating > 0 && (
                      <span className="review-form__rating-label">{RATING_LABELS[reviewRating]}</span>
                    )}
                  </div>
                  <textarea
                    className="review-form__textarea"
                    rows={4}
                    placeholder="Share your experience with this product..."
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                  />
                  <div className="review-form__actions">
                    <button
                      className="btn btn--primary"
                      onClick={handleSubmitReview}
                      disabled={isSubmitting || reviewRating === 0 || !reviewComment.trim()}
                    >
                      {isSubmitting ? 'Submitting...' : isEditing ? 'Update Review' : 'Submit Review'}
                    </button>
                    {isEditing && (
                      <button
                        className="btn btn--outline"
                        onClick={() => { setIsEditing(false); setEditingId(null); setReviewRating(0); setReviewComment(''); setHasReviewed(true); }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              <hr className="reviews-divider" />

              {/* Reviews List */}
              <div>
                <h3 className="reviews-list__title">Customer Reviews ({reviews.length})</h3>
                {reviews.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>
                    No reviews yet. Be the first to review this product!
                  </p>
                ) : (
                  <>
                    {visibleReviews.map((review) => {
                      const isMine = user?.userId === review.user_id;
                      return (
                        <div key={review.id} className="review-card">
                          <div className="review-card__header">
                            <div className="review-card__avatar">
                              {(review.customer_name?.[0] || 'U').toUpperCase()}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <StarRating rating={review.rating} size={14} />
                                <span className="review-card__title">{review.title}</span>
                                {review.is_verified_purchase && (
                                  <span className="review-card__verified">✓ Verified</span>
                                )}
                              </div>
                              <p className="review-card__meta">
                                By {review.customer_name} • {new Date(review.created_at).toLocaleDateString('en-GB')}
                              </p>
                            </div>
                            {isMine && (
                              <div className="review-card__actions">
                                <button className="review-card__btn" onClick={() => handleEditReview(review)}>Edit</button>
                                <button className="review-card__btn review-card__btn--danger" onClick={() => handleDeleteReview(review.id)}>Delete</button>
                              </div>
                            )}
                          </div>
                          <p className="review-card__comment">{review.comment}</p>
                        </div>
                      );
                    })}
                    {reviews.length > 3 && (
                      <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <button className="btn btn--outline" onClick={() => setShowAll(!showAll)}>
                          {showAll ? 'Show Less' : `Show More (${reviews.length - 3} more)`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image zoom lightbox — centered modal with thumbnail rail */}
      {lightboxOpen && images.length > 0 && (
        <div
          onClick={() => { setLightboxOpen(false); setZoomed(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(17,24,39,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, userSelect: 'none',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative', display: 'flex',
              width: 'min(1040px, 94vw)', height: 'min(640px, 88vh)',
              background: '#ffffff', borderRadius: 14, overflow: 'hidden',
              boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            }}
          >
            {/* Close button */}
            <button
              onClick={() => { setLightboxOpen(false); setZoomed(false); }}
              aria-label="Close"
              style={{
                position: 'absolute', top: 14, right: 16, zIndex: 3,
                width: 38, height: 38, borderRadius: '50%', border: '1px solid #e5e7eb',
                background: '#fff', color: '#111827',
                fontSize: 24, lineHeight: 1, cursor: 'pointer',
              }}
            >
              ×
            </button>

            {/* Left thumbnail rail — all product images */}
            <style>{`
              .lb-thumb-rail { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
              .lb-thumb-rail::-webkit-scrollbar { width: 4px; }
              .lb-thumb-rail::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
              .lb-thumb-rail::-webkit-scrollbar-track { background: transparent; }
            `}</style>
            <div
              className="lb-thumb-rail"
              style={{
                width: 104, flexShrink: 0, borderRight: '1px solid #eef0f2',
                overflowY: 'auto', overflowX: 'hidden', padding: '14px 12px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              }}
            >
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setZoomed(false); setSelectedImage(i); }}
                  style={{
                    width: 68, height: 68, flexShrink: 0, padding: 4, cursor: 'pointer',
                    background: '#fff', borderRadius: 8,
                    border: selectedImage === i ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                  }}
                >
                  <img
                    src={img}
                    alt={`${product.product_name} ${i + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </button>
              ))}
            </div>

            {/* Main image area */}
            <div
              style={{
                flex: 1, position: 'relative', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                padding: 48, overflow: 'hidden',
              }}
            >
              {/* Model number */}
              <div
                style={{
                  position: 'absolute', top: 16, left: 20, zIndex: 2,
                  color: '#111827', fontSize: 14, fontWeight: 600, letterSpacing: 0.3,
                }}
              >
                {product.model_no ? `Model No: ${product.model_no}` : product.product_name}
              </div>

              {/* Prev */}
              {images.length > 1 && (
                <button
                  onClick={() => {
                    setZoomed(false);
                    setSelectedImage((i) => (i - 1 + images.length) % images.length);
                  }}
                  aria-label="Previous image"
                  style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    width: 44, height: 44, borderRadius: '50%', border: '1px solid #e5e7eb',
                    background: '#f9fafb', color: '#111827', fontSize: 26, cursor: 'pointer', zIndex: 2,
                  }}
                >
                  ‹
                </button>
              )}

              {/* Zoomable image — contained, not full width/height */}
              <img
                src={images[selectedImage]}
                alt={product.product_name}
                onClick={() => setZoomed((z) => !z)}
                onMouseMove={(e) => {
                  if (!zoomed) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - r.left) / r.width) * 100;
                  const y = ((e.clientY - r.top) / r.height) * 100;
                  setZoomOrigin({ x, y });
                }}
                style={{
                  maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                  transform: zoomed ? 'scale(2.2)' : 'scale(1)',
                  transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                  transition: zoomed ? 'transform 0.05s ease-out' : 'transform 0.2s ease-out',
                  cursor: zoomed ? 'zoom-out' : 'zoom-in',
                }}
              />

              {/* Next */}
              {images.length > 1 && (
                <button
                  onClick={() => {
                    setZoomed(false);
                    setSelectedImage((i) => (i + 1) % images.length);
                  }}
                  aria-label="Next image"
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    width: 44, height: 44, borderRadius: '50%', border: '1px solid #e5e7eb',
                    background: '#f9fafb', color: '#111827', fontSize: 26, cursor: 'pointer', zIndex: 2,
                  }}
                >
                  ›
                </button>
              )}

              {/* Counter + hint */}
              <div
                style={{
                  position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
                  color: '#6b7280', fontSize: 12.5, letterSpacing: 0.3,
                  background: '#f3f4f6', padding: '5px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                }}
              >
                {images.length > 1 ? `${selectedImage + 1} / ${images.length} • ` : ''}
                {zoomed ? 'Click to zoom out' : 'Click image to zoom'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Virtual Try-On modal — only mounted when the org feature is enabled. */}
      {tryonEnabled && (
        <TryonModal open={tryonOpen} onClose={() => setTryonOpen(false)} product={product} />
      )}
    </div>
  );
};

export default ProductDetailPage;
